import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useSellerBoost, SellerBoostItem } from '../../hooks/useSellerBoost';
import { getMarketplaceConnectionDisplayName } from '../../utils/marketplaceConnections';
import { SellerRunButton, sellerHeaderSelectClass } from './SellerAutomationHeader';
import { Drawer, useSellerSettings } from './sellerUi';

// Impulsionar deixou de ser uma tela: sao 5 vagas por conta Shopee, entao vive
// num painel aberto pelo botao "Impulsionar x/5" da tela Anuncios. O estado fica
// no hook useShopeeBoost para o botao mostrar as vagas sem abrir o painel.

type ShopeeConnection = { id: string; connectionName?: string | null; shopName?: string | null; shopId?: number; status?: string };

function headers() {
  const token = localStorage.getItem('auth_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function formatMoney(value?: number | null) {
  if (!value) return '-';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function useShopeeBoost(enabled: boolean, poll = false) {
  const [connections, setConnections] = useState<ShopeeConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    fetch('/api/shopee/connections', { headers: headers() })
      .then((res) => res.ok ? res.json() : [])
      .then((json) => {
        if (!active) return;
        const list = Array.isArray(json) ? json : (json.data || json.connections || []);
        const activeConnections = list.filter((item: ShopeeConnection) => item.status !== 'revoked');
        setConnections(activeConnections);
        setConnectionId((current) => current || activeConnections[0]?.id || '');
      })
      .catch(() => setConnections([]));
    return () => { active = false; };
  }, [enabled]);

  const boost = useSellerBoost(enabled && connectionId ? connectionId : undefined, { poll });
  return { connections, connectionId, setConnectionId, ...boost };
}

export type ShopeeBoostState = ReturnType<typeof useShopeeBoost>;

function ItemRow({ item, selected, disabled, onToggle }: { item: SellerBoostItem; selected: boolean; disabled?: boolean; onToggle: () => void }) {
  return (
    <button type="button" disabled={disabled} onClick={onToggle} className={`flex w-full min-w-0 items-center gap-3 rounded-md border px-3 py-2 text-left transition ${selected ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-white hover:border-gray-300'} ${disabled ? 'cursor-not-allowed opacity-70' : ''}`}>
      {!item.isBoosted && <input type="checkbox" checked={selected} disabled={disabled} onChange={onToggle} className="h-4 w-4 shrink-0" onClick={(e) => e.stopPropagation()} />}
      <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-gray-100">
        {item.thumbnailUrl ? <img src={item.thumbnailUrl} alt="" className="h-full w-full object-cover" /> : null}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-gray-900">{item.title}</div>
        <div className="mt-0.5 flex flex-wrap gap-2 text-xs text-gray-500">
          <span>ID {item.itemId}</span>
          {item.sku ? <span>SKU {item.sku}</span> : null}
          <span>{formatMoney(item.price)}</span>
        </div>
      </div>
      {item.isBoosted ? <span className="shrink-0 rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-700">impulsionado</span> : null}
    </button>
  );
}

export function SellerBoostDrawer({ state, onClose }: { state: ShopeeBoostState; onClose: () => void }) {
  const { connections, connectionId, setConnectionId, data, loading, saving, error: loadError, boostItems, runAutoBoostNow } = state;
  const { openSettings, canConfigure } = useSellerSettings();
  const [manualSelected, setManualSelected] = useState<Set<string>>(() => new Set());
  const [runningQueue, setRunningQueue] = useState(false);
  const [search, setSearch] = useState('');

  const availableItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.items || []).filter((item) => !item.isBoosted && (!q || item.title.toLowerCase().includes(q) || item.itemId.includes(q) || (item.sku || '').toLowerCase().includes(q)));
  }, [data?.items, search]);
  const boostedItems = useMemo(() => (data?.items || []).filter((item) => item.isBoosted), [data?.items]);
  const manualLimit = Math.max(0, data?.availableSlots ?? 5);
  const used = data?.boostedCount || 0;

  const toggleManual = (itemId: string) => {
    setManualSelected((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else if (next.size < manualLimit) next.add(itemId);
      return next;
    });
  };

  const runManual = async () => {
    try {
      const result = await boostItems(Array.from(manualSelected));
      toast.success(`Impulsionamento enviado: ${(result.successList || []).length} sucesso(s), ${(result.failureList || []).length} falha(s).`);
      setManualSelected(new Set());
    } catch (err: any) {
      toast.error(err.message || 'Erro ao impulsionar');
    }
  };

  const runQueueNow = async () => {
    setRunningQueue(true);
    try {
      const json = await runAutoBoostNow();
      toast.success(json?.result?.message || 'Fila automática executada.');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao executar fila automática');
    } finally {
      setRunningQueue(false);
    }
  };

  const queueIds = data?.autoBoost?.itemIds || [];
  const autoOn = Boolean(data?.autoBoost?.enabled && queueIds.length);
  const queueItems = useMemo(() => {
    const ids = new Set(data?.autoBoost?.itemIds || []);
    return (data?.items || []).filter((item) => ids.has(item.itemId));
  }, [data?.items, data?.autoBoost?.itemIds]);

  useEffect(() => {
    if (loadError) toast.error(loadError);
  }, [loadError]);

  return (
    <Drawer
      open
      onClose={onClose}
      title="Impulsionar na Shopee"
      subtitle="A Shopee dá 5 vagas de destaque por conta, por 4 horas cada."
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-gray-500">{manualSelected.size ? `${manualSelected.size} selecionado(s)` : `Escolha até ${manualLimit} anúncio(s)`}</span>
          <SellerRunButton label={`Impulsionar ${manualSelected.size || ''}`.trim()} running={saving} disabled={manualSelected.size === 0} variant="primary" onClick={runManual} />
        </div>
      }
    >
      <div className="space-y-4">
        {connections.length > 1 && (
          <select value={connectionId} onChange={(e) => { setConnectionId(e.target.value); setManualSelected(new Set()); }} className={`${sellerHeaderSelectClass} w-full`}>
            {connections.map((connection) => <option key={connection.id} value={connection.id}>{getMarketplaceConnectionDisplayName('shopee', connection)}</option>)}
          </select>
        )}

        <section className="rounded-lg border border-gray-200 p-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold text-gray-800">Vagas em uso</h3>
            <span className="text-xs text-gray-500">{used}/5</span>
          </div>
          <div className="mt-2 flex gap-1.5">{Array.from({ length: 5 }).map((_, index) => <div key={index} className={`h-2 flex-1 rounded-full ${index < used ? 'bg-orange-500' : 'bg-gray-200'}`} />)}</div>
          <div className="mt-3 rounded-md bg-gray-50 px-2.5 py-2 text-[11px] text-gray-600">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                <span className={`mr-1.5 inline-block h-1.5 w-1.5 rounded-full ${autoOn ? 'bg-green-500' : 'bg-gray-300'}`} />
                Fila automática {autoOn ? 'ligada' : 'desligada'} · {queueIds.length} anúncio(s) na fila
                {autoOn && data?.autoBoost?.nextBoostAt ? ` · próxima rodada ${new Date(data.autoBoost.nextBoostAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}
              </span>
              <span className="flex gap-2">
                {autoOn && <SellerRunButton label="Rodar fila agora" runningLabel="Rodando..." running={runningQueue} onClick={runQueueNow} />}
                {canConfigure && <button type="button" onClick={() => openSettings('impulsionar')} className="font-semibold text-orange-700 hover:underline">Editar fila</button>}
              </span>
            </div>
            {/* Os anúncios salvos na fila, para conferir sem abrir a configuração. */}
            {queueItems.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {queueItems.map((item) => (
                  <span key={item.itemId} title={item.title} className="inline-flex max-w-[220px] items-center gap-1.5 rounded-full border border-gray-200 bg-white py-0.5 pl-0.5 pr-2">
                    {item.thumbnailUrl ? <img src={item.thumbnailUrl} alt="" className="h-5 w-5 rounded-full object-cover" /> : <span className="h-5 w-5 rounded-full bg-gray-100" />}
                    <span className="truncate">{item.title}</span>
                  </span>
                ))}
              </div>
            )}
            {queueIds.length > queueItems.length && <p className="mt-1.5 text-amber-700">{queueIds.length - queueItems.length} anúncio(s) da fila não estão sincronizados nesta conta.</p>}
          </div>
        </section>

        {loading && !data ? <div className="py-8 text-center text-sm text-gray-500">Carregando anúncios...</div> : null}

        {boostedItems.length > 0 && (
          <div className="space-y-2">
            <div className="text-[11px] font-semibold uppercase text-orange-700">Em destaque agora</div>
            {boostedItems.map((item) => <ItemRow key={item.itemId} item={item} selected={false} disabled onToggle={() => null} />)}
          </div>
        )}

        {(data?.items || []).length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] font-semibold uppercase text-gray-500">Disponíveis</div>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por título, ID ou SKU" className="w-56 rounded-md border border-gray-200 px-2.5 py-1 text-xs focus:border-orange-300 focus:outline-none" />
            </div>
            {availableItems.map((item) => <ItemRow key={item.itemId} item={item} selected={manualSelected.has(item.itemId)} disabled={!manualSelected.has(item.itemId) && manualSelected.size >= manualLimit} onToggle={() => toggleManual(item.itemId)} />)}
          </div>
        )}
        {!loading && !data?.items?.length ? <div className="py-8 text-center text-sm text-gray-500">{connections.length === 0 ? 'Nenhuma conta Shopee conectada.' : 'Nenhum anúncio sincronizado nesta conta. Sincronize as métricas antes de impulsionar.'}</div> : null}
      </div>
    </Drawer>
  );
}
